import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { mumsApi } from "./mumsApi";
import { MumDrawer, PhotoField, FormSection } from "./MumDrawer";
import { Plus, Search, Edit3, Trash2, Send, Loader2, Sprout } from "lucide-react";
import { toast } from "sonner";

const TUBER_STATUSES = ["In Ground", "Dug Up", "Divided", "Sold", "Gifted"];
const TUBER_CONDITIONS = ["Excellent", "Good", "Fair", "Poor"];
const STATUS_TONES = {
  "In Ground": "bg-[var(--mum-sky)] text-[var(--mum-sapphire)]",
  "Dug Up": "bg-[var(--mum-cornflower)]/30 text-[var(--mum-sapphire)]",
  "Divided": "bg-[var(--mum-sapphire)]/15 text-[var(--mum-sapphire)]",
  "Sold": "bg-[var(--mum-cobalt)] text-[var(--mum-ivory)]",
  "Gifted": "bg-[var(--mum-silver)] text-[var(--mum-cobalt)]",
};

const emptyTuber = {
  variety_name: "",
  status: "In Ground",
  condition: "Good",
  viable_count: 1,
  garden_bed: "",
  row: "",
  date_dug: "",
  storage_location: "",
  date_stored: "",
  value_per_tuber: 0,
  notes: "",
  bloom_photo: null,
  tuber_photo: null,
  extra_photos: [],
};

export default function MumsTubers() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [drawer, setDrawer] = useState(() =>
    new URLSearchParams(window.location.search).get("add") === "1" ? "new" : null
  );

  const load = async () => {
    setLoading(true);
    try { setItems(await mumsApi.listTubers()); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await mumsApi.listTubers();
        if (!cancelled) setItems(r);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    if (params.get("add") === "1") {
      params.delete("add");
      setParams(params, { replace: true });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((i) => (filterStatus ? i.status === filterStatus : true))
      .filter((i) => (q ? [i.variety_name, i.garden_bed, i.storage_location].some((s) => (s || "").toLowerCase().includes(q)) : true));
  }, [items, search, filterStatus]);

  const totalViable = filtered.reduce((a, b) => a + (b.viable_count || 0), 0);
  const totalValue = filtered.reduce((a, b) => a + (b.total_value || 0), 0);

  const onDelete = async (id) => {
    if (!window.confirm("Remove this tuber record?")) return;
    await mumsApi.deleteTuber(id);
    toast.success("Tuber removed");
    await load();
  };

  const onSend = async (id) => {
    await mumsApi.sendToBloom(id);
    toast.success("Sent to Dahlia Blooms");
  };

  return (
    <div className="space-y-6" data-testid="mums-tubers-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="mum-chip"><Sprout size={12} /> Tab 1</div>
          <h1 className="font-display text-4xl mt-2">Dahlia Tubers</h1>
          <p className="font-display italic text-[var(--mum-cobalt)] text-sm mt-1">
            {filtered.length} record{filtered.length === 1 ? "" : "s"} · {totalViable} viable · ${totalValue.toLocaleString()} estimated value
          </p>
        </div>
        <button className="mum-btn" onClick={() => setDrawer("new")} data-testid="mums-tuber-add">
          <Plus size={15} /> Add Tuber
        </button>
      </header>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mum-cobalt)]/60" />
          <input
            className="mum-field pl-9"
            placeholder="Search by variety, bed, storage…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="mums-tuber-search"
          />
        </div>
        <select
          className="mum-field sm:w-56"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="mums-tuber-filter-status"
        >
          <option value="">All statuses</option>
          {TUBER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto text-[var(--mum-cobalt)]" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState onAdd={() => setDrawer("new")} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mums-tuber-grid">
          {filtered.map((t) => (
            <TuberCard key={t.id} t={t} onEdit={() => setDrawer({ edit: t })} onDelete={() => onDelete(t.id)} onSend={() => onSend(t.id)} />
          ))}
        </div>
      )}

      <MumDrawer
        open={drawer !== null}
        onClose={() => setDrawer(null)}
        title={drawer === "new" ? "Add Tuber" : drawer?.edit ? "Edit Tuber" : "Add Tuber"}
        testid="mums-tuber-drawer"
      >
        <TuberForm
          initial={drawer?.edit || emptyTuber}
          onClose={() => setDrawer(null)}
          onSaved={async () => { setDrawer(null); await load(); }}
        />
      </MumDrawer>
    </div>
  );
}

function TuberCard({ t, onEdit, onDelete, onSend }) {
  const photo = t.bloom_photo || t.tuber_photo || (t.extra_photos || [])[0];
  return (
    <article className="mums-card overflow-hidden flex flex-col" data-testid={`tuber-${t.id}`}>
      <div className="aspect-[4/3] bg-[var(--mum-sky)]/50 flex items-center justify-center">
        {photo ? (
          <img src={photo} alt={t.variety_name} className="w-full h-full object-cover" />
        ) : (
          <Sprout size={36} className="text-[var(--mum-cobalt)]/50" />
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-xl text-[var(--mum-sapphire)] leading-tight" data-testid={`tuber-name-${t.id}`}>{t.variety_name}</h3>
          <span className={`text-[0.65rem] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_TONES[t.status] || "bg-[var(--mum-sky)]"}`}>{t.status}</span>
        </div>
        <p className="text-xs text-[var(--mum-cobalt)]/75 mt-1">
          {t.viable_count} viable · {t.condition}
          {t.garden_bed ? ` · Bed ${t.garden_bed}` : ""}
        </p>
        {t.storage_location && <p className="text-xs text-[var(--mum-cobalt)]/75 mt-0.5">Stored: {t.storage_location}</p>}
        {!!t.total_value && <p className="text-xs text-[var(--mum-cobalt)] mt-0.5">${t.total_value} value</p>}
        {t.notes && <p className="text-sm mt-2 line-clamp-3">{t.notes}</p>}
        <div className="flex gap-2 mt-auto pt-3">
          <button className="mum-btn-ghost flex-1" onClick={onEdit} data-testid={`tuber-edit-${t.id}`}><Edit3 size={13} /> Edit</button>
          <button className="mum-btn-ghost" onClick={onSend} title="Send to Dahlia Blooms" data-testid={`tuber-send-${t.id}`}><Send size={13} /></button>
          <button className="mum-btn-ghost text-[var(--mum-sapphire)]" onClick={onDelete} data-testid={`tuber-delete-${t.id}`}><Trash2 size={13} /></button>
        </div>
      </div>
    </article>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="mums-card-quiet p-10 text-center" data-testid="mums-tuber-empty">
      <Sprout size={36} className="text-[var(--mum-cobalt)]/60 mx-auto" />
      <p className="font-display italic text-[var(--mum-cobalt)] text-lg mt-3">
        Your tuber record is empty.
      </p>
      <button className="mum-btn mt-5" onClick={onAdd} data-testid="mums-tuber-add-first">
        <Plus size={15} /> Add first tuber
      </button>
    </div>
  );
}

function TuberForm({ initial, onClose, onSaved }) {
  const editing = !!initial.id;
  const [form, setForm] = useState(() => ({ ...emptyTuber, ...initial }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.variety_name.trim()) { toast.error("Variety name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        viable_count: Number(form.viable_count) || 0,
        value_per_tuber: Number(form.value_per_tuber) || 0,
      };
      if (editing) await mumsApi.updateTuber(initial.id, payload);
      else await mumsApi.createTuber(payload);
      toast.success(editing ? "Tuber updated" : "Tuber added");
      onSaved();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Couldn't save the tuber");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="tuber-form">
      <FormSection title="The Variety">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Variety name *</span>
          <input className="mum-field mt-1.5" value={form.variety_name} onChange={(e) => set("variety_name", e.target.value)} required data-testid="tuber-variety-name" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Status</span>
            <select className="mum-field mt-1.5" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="tuber-status">
              {TUBER_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Condition</span>
            <select className="mum-field mt-1.5" value={form.condition} onChange={(e) => set("condition", e.target.value)}>
              {TUBER_CONDITIONS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <label className="block col-span-1">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Viable</span>
            <input type="number" min="0" className="mum-field mt-1.5" value={form.viable_count} onChange={(e) => set("viable_count", e.target.value)} data-testid="tuber-viable" />
          </label>
          <label className="block col-span-1">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Bed</span>
            <input className="mum-field mt-1.5" value={form.garden_bed} onChange={(e) => set("garden_bed", e.target.value)} />
          </label>
          <label className="block col-span-1">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Row</span>
            <input className="mum-field mt-1.5" value={form.row} onChange={(e) => set("row", e.target.value)} />
          </label>
        </div>
      </FormSection>

      <FormSection title="Storage">
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Date dug</span>
            <input type="date" className="mum-field mt-1.5" value={form.date_dug || ""} onChange={(e) => set("date_dug", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Date stored</span>
            <input type="date" className="mum-field mt-1.5" value={form.date_stored || ""} onChange={(e) => set("date_stored", e.target.value)} />
          </label>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Storage location</span>
          <input className="mum-field mt-1.5" placeholder="Crate 4 · Shed cupboard" value={form.storage_location} onChange={(e) => set("storage_location", e.target.value)} />
        </label>
      </FormSection>

      <FormSection title="Value">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Estimated value per tuber ($)</span>
          <input type="number" min="0" step="0.5" className="mum-field mt-1.5" value={form.value_per_tuber} onChange={(e) => set("value_per_tuber", e.target.value)} />
        </label>
        <p className="text-xs text-[var(--mum-cobalt)]/70">
          Total estimated value: <strong>${(Number(form.viable_count) * Number(form.value_per_tuber) || 0).toFixed(2)}</strong>
        </p>
      </FormSection>

      <FormSection title="Photos">
        <PhotoField label="Bloom photo" value={form.bloom_photo} onChange={(v) => set("bloom_photo", v)} testid="tuber-photo-bloom" />
        <PhotoField label="Tuber photo" value={form.tuber_photo} onChange={(v) => set("tuber_photo", v)} testid="tuber-photo-tuber" />
        <PhotoField label="Additional photos" value={form.extra_photos} onChange={(v) => set("extra_photos", v)} multi testid="tuber-photo-extra" />
      </FormSection>

      <FormSection title="Notes">
        <textarea className="mum-field" rows={4} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Anything to remember for next year…" />
      </FormSection>

      <div className="flex gap-2 sticky bottom-0 bg-[var(--mum-paper)] pt-3 -mx-5 px-5 pb-1 border-t border-[var(--mum-sky)]">
        <button type="button" className="mum-btn-ghost flex-1" onClick={onClose}>Cancel</button>
        <button type="submit" className="mum-btn flex-1" disabled={busy} data-testid="tuber-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {editing ? "Save changes" : "Add tuber"}
        </button>
      </div>
    </form>
  );
}
